import React, { useState, useEffect } from 'react';
import { DatePicker, Button } from 'antd';
import dayjs from '../../utils/dayjs-config';
import {
  LeftOutlined,
  RightOutlined,
  DownOutlined
} from '@ant-design/icons';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import { getDateRange } from './getDateRange';
import { getTimezone } from '../../api/store';
import { useSearchParams } from 'react-router-dom';

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);

/**
 * 切换到日报，默认选中今日
 * 2025.07.23
 * 向左侧切换，选中昨日
 * 向右侧切换，选中明日
 * 
 * 切换到周报，默认选中本周
 * 比如 7.23 为周三，则默认选中 7.21 - 7.23 (本周的周一 到 今天)
 * 向左切换是 上周的周一到周日 
 * 向右切换是 下周的周一到周日
 * 
 * 切换到月报，默认选中当月
 * 比如 如果今天是7.23 ，则默认选中 7.1 - 7.23 (当前月份的第一天 到 今天)
 * 向左切换是上个月的1号-最后1天
 * 向右切换是下个月的1号-最后1天
 * 
 * 切换到季报，默认选中当前季度 (Q1 1月1号 - 3月31号  Q2 4月1号 - 6月30号  Q3 7月1号 - 9月30号  Q4 10月1号 - 12月31号)
 * 比如 如果今天是7.23，是第三季度 ，则默认选中 7.1 - 7.23(当前季度的第一天 到 今天)
 * 向左切换是上个季度的第一天到最后一天
 * 向右切换是下个季度的第一天到最后一天
 * 
 * 切换到年报，默认选中当前年
 * 比如 如果今天是2025.7.23 ，则默认选中 2025.1.1 - 2025.7.23
 * 向左切换是上一年的1.1-12.31
 * 向右切换是下一年的1.1-12.31
 */

const dateFormat = 'YYYY/MM/DD';

let today = null;
const SelectData = ({ type = 'day', onDateChange, isTodayChange }) => {
  const [searchParams] = useSearchParams()
  const defaultTimezone = "America/Mexico_City";
  // 使用配置了时区的dayjs实例初始化日期
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [startDate, setStartDate] = useState(dayjs().tz(timezone));
  const [endDate, setEndDate] = useState(dayjs().tz(timezone));
  const [periodOffset, setPeriodOffset] = useState(0);
  const ip = searchParams.get('ip')
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timezoneFetched, setTimezoneFetched] = useState(false); // 追踪时区是否已获取


  // 设置时区，但是时区没有发生变化 手动触发 onDateChange 事件
  function emitDateChange(newTimezone) {
    if (newTimezone === defaultTimezone) {
      onDateChange([startDate, endDate]);
    }
  }

  // 判断开始日期和结束日期是否都为今天
  function startEndIsToday() {
    if (!today) return true;
    const format = 'YYYY/MM/DD';
    const todayString = today.format(format);
    return todayString === startDate.format(format) && todayString === endDate.format(format);
  }

  useEffect(() => {
    // 将是否为今天传给父组件
    startEndIsToday() ? isTodayChange(true) : isTodayChange(false)
  }, [startDate, endDate]);


  // Fetch server timezone only when ip changes
  useEffect(() => {
    const fetchTimezone = async () => {
      setLoading(true);
      try {
        const response = await getTimezone(ip);
        let newTimezone = response.data;
        if (!response.data) {
          newTimezone = defaultTimezone
        }
        setTimezone(newTimezone);
        emitDateChange(newTimezone)

        setTimezoneFetched(true); // 时区获取成功
        today = dayjs().tz(newTimezone);
      } catch (err) {
        setTimezoneFetched(true); // 时区获取成功

        setTimezone(defaultTimezone);
        emitDateChange(defaultTimezone)
        today = dayjs().tz(defaultTimezone);
        setError('Failed to fetch timezone. Using guessed timezone.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTimezone();
  }, [ip]);

  useEffect(() => {
    setPeriodOffset(0);
  }, [type]);

  useEffect(() => {
    if (!timezoneFetched || !timezone) {
      return;
    }
    // 确保使用配置了时区的dayjs实例
    const today = dayjs().tz(timezone);
    const [newStart, newEnd] = getDateRange(type, periodOffset, today);
    if (!newStart.isSame(startDate) || !newEnd.isSame(endDate)) {
      // 确保日期对象应用了正确的时区
      setStartDate(newStart.tz(timezone));
      setEndDate(newEnd.tz(timezone));
      onDateChange([newStart.tz(timezone), newEnd.tz(timezone)]);
    }
  }, [type, periodOffset, timezone]);

  const handleStartDateChange = (date) => {
    if (!timezoneFetched || !timezone) {
      return;
    }
    // 确保日期对象应用了正确的时区
    const dateWithTz = date.tz(timezone);
    setStartDate(dateWithTz);
    onDateChange([dateWithTz, endDate]);
  };

  const handleEndDateChange = (date) => {
    if (!timezoneFetched || !timezone) {
      return;
    }
    // 确保日期对象应用了正确的时区
    const dateWithTz = date.tz(timezone);
    setEndDate(dateWithTz);
    onDateChange([startDate, dateWithTz]);
  };

  const shiftDates = (amount) => {

    setPeriodOffset(prev => prev + amount);
  };

  const handleLeftArrow = () => shiftDates(-1);
  const handleRightArrow = () => shiftDates(1);

  const downIcon = <DownOutlined />;

  return (
    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: "#fff", padding: '4px' }} >
      <Button onClick={handleLeftArrow}>
        <LeftOutlined />
      </Button>
      {
        loading ?
          (<div></div>)
          : (<>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              format={dateFormat}
              style={{ margin: '0 8px' }}
              suffixIcon={downIcon}
            />
            -
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              format={dateFormat}
              style={{ margin: '0 8px' }}
              suffixIcon={downIcon}
            />
          </>)

      }
      <Button onClick={handleRightArrow}>
        <RightOutlined />
      </Button>
    </div>
  );
};

export default SelectData;
